import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Card,
  CardContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { searchVisitors, checkOut, Visitor, getErrorMessage } from '../visitorKioskApi';

// Fontana brand color
const FONTANA_BLUE = '#1a365d';

interface CheckOutTabProps {
  onSuccess: () => void;
}

export default function CheckOutTab({ onSuccess }: CheckOutTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [noResults, setNoResults] = useState(false);

  const handleSearch = useCallback(async (query: string) => {
    if (query.trim().length === 0) {
      setVisitors([]);
      setNoResults(false);
      return;
    }

    setLoading(true);
    setError(null);
    setNoResults(false);
    try {
      const result = await searchVisitors(query.trim());
      setVisitors(result.visitors);
      setNoResults(result.visitors.length === 0);
    } catch (err) {
      setError(getErrorMessage(err));
      setVisitors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (value: string) => {
    setSearchQuery(value);
    const timeoutId = setTimeout(() => {
      handleSearch(value);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const handleSelectVisitor = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
  };

  const handleConfirmCheckOut = async () => {
    if (!selectedVisitor) return;

    setCheckingOut(true);
    setError(null);
    try {
      await checkOut(selectedVisitor._id);
      setSelectedVisitor(null);
      setSearchQuery('');
      setVisitors([]);
      setNoResults(false);
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCheckingOut(false);
    }
  };

  const handleCancelCheckOut = () => {
    setSelectedVisitor(null);
  };

  const formatCheckInTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Avatar sx={{ bgcolor: '#FFEBEE', width: 64, height: 64, mx: 'auto', mb: 2 }}>
            <ExitToAppIcon sx={{ fontSize: 32, color: '#E53935' }} />
          </Avatar>
          
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Besöksutcheckning
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Hitta ditt namn och tryck för att checka ut
          </Typography>

          <TextField
            fullWidth
            placeholder="Sök på namn eller företag..."
            value={searchQuery}
            onChange={(e) => handleInputChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
            sx={{
              mb: 2,
              '& .MuiInputBase-root': { 
                minHeight: 56,
                borderRadius: 2,
              },
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': { borderColor: FONTANA_BLUE },
                '&.Mui-focused fieldset': { borderColor: FONTANA_BLUE },
              },
            }}
          />

          {visitors.length > 0 && (
            <List sx={{ 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: 'divider',
              textAlign: 'left',
              maxHeight: 280,
              overflow: 'auto',
            }}>
              {visitors.map((visitor, index) => (
                <ListItemButton
                  key={visitor._id}
                  onClick={() => handleSelectVisitor(visitor)}
                  sx={{
                    minHeight: 68,
                    borderBottom: index < visitors.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#E8EEF4', width: 44, height: 44 }}>
                      <PersonIcon sx={{ color: FONTANA_BLUE }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {visitor.fullName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {visitor.company && `${visitor.company} • `}
                        Incheckad kl {formatCheckInTime(visitor.checkInAt)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}

          {noResults && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, py: 3 }}>
              Inga besökare hittades. Kontrollera stavningen.
            </Typography>
          )}

          {searchQuery.trim().length === 0 && !loading && (
            <Box sx={{ py: 4 }}>
              <PersonIcon sx={{ fontSize: 56, color: 'grey.300', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Inga besökare i byggnaden just nu
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={!!selectedVisitor}
        onClose={handleCancelCheckOut}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Bekräfta utcheckning
        </DialogTitle>
        <DialogContent>
          {selectedVisitor && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Namn:</strong> {selectedVisitor.fullName}
              </Typography>
              {selectedVisitor.company && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Företag:</strong> {selectedVisitor.company}
                </Typography>
              )}
              <Typography variant="body1">
                <strong>Incheckad:</strong> {formatCheckInTime(selectedVisitor.checkInAt)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={handleCancelCheckOut}
            variant="outlined"
            size="large"
            sx={{ minHeight: 52, minWidth: 120, borderRadius: 2 }}
          >
            Avbryt
          </Button>
          <Button
            onClick={handleConfirmCheckOut}
            variant="contained"
            size="large"
            disabled={checkingOut}
            startIcon={checkingOut ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />}
            sx={{ 
              minHeight: 52, 
              minWidth: 160, 
              borderRadius: 2,
              bgcolor: FONTANA_BLUE,
              '&:hover': { bgcolor: '#0f2744' },
            }}
          >
            {checkingOut ? 'Checkar ut...' : 'Checka ut'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
